import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useCreateCompany, useUpdateCompany } from '../../hooks/useCompanies.js';
import { useFieldDefinitions } from '../../hooks/useFieldDefinitions.js';
import { useValidationRules } from '../../hooks/useValidationRules.js';
import { runValidationRules } from '../../lib/validationEngine.js';
import { toast } from '../../lib/toast.js';
import CustomFieldInput from '../customFields/CustomFieldInput.js';
import type { Company, CompanyType } from '@mayksel/types';

interface Props {
  company?: Company | null;
  onClose: () => void;
}

const COMPANY_TYPES: CompanyType[] = ['prospect', 'customer', 'partner', 'vendor'];

interface FormState {
  name: string;
  type: CompanyType[];
  website: string;
  phone: string;
  email: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  notes: string;
}

export default function CompanyModal({ company, onClose }: Props) {
  const { t } = useTranslation('companies');
  const overlayRef = useRef<HTMLDivElement>(null);

  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany(company?.id ?? '');
  const { data: customFieldDefs = [] } = useFieldDefinitions('company');
  const { data: validationRulesList = [] } = useValidationRules('company');
  const activeCustomFields = customFieldDefs.filter((f) => f.isActive);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>(
    company?.customFields ?? {},
  );

  const [form, setForm] = useState<FormState>({
    name: company?.name ?? '',
    type: company?.type ?? ['prospect'],
    website: company?.website ?? '',
    phone: company?.phone ?? '',
    email: company?.email ?? '',
    addressLine1: company?.addressLine1 ?? '',
    city: company?.city ?? '',
    state: company?.state ?? '',
    zip: company?.zip ?? '',
    country: company?.country ?? 'US',
    notes: company?.notes ?? '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function toggleType(type: CompanyType) {
    setForm((prev) => {
      const current = prev.type;
      if (current.includes(type)) {
        return { ...prev, type: current.length > 1 ? current.filter((t) => t !== type) : current };
      }
      return { ...prev, type: [...current, type] };
    });
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Company name is required';
    if (form.website && !form.website.startsWith('http')) {
      newErrors.website = 'Must start with http:// or https://';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const trigger = company ? 'on_update' : 'on_create';
    const entityData: Record<string, unknown> = {
      name: form.name.trim(),
      type: form.type,
      website: form.website.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      notes: form.notes.trim(),
      ...customFieldValues,
    };
    const violations = runValidationRules(validationRulesList, entityData, trigger);
    const blocking = violations.filter((v) => v.actionType !== 'show_warning');
    if (blocking.length > 0) {
      blocking.forEach((v) => toast.error(v.message));
      return;
    }
    violations.filter((v) => v.actionType === 'show_warning').forEach((v) => toast.error(v.message));

    const data = {
      name: form.name.trim(),
      type: form.type,
      website: form.website.trim() || undefined,
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      zip: form.zip.trim() || undefined,
      country: form.country || 'US',
      notes: form.notes.trim() || undefined,
      tags: [] as string[],
      isWhiteLabel: false,
      customFields: customFieldValues,
    };

    try {
      if (company) {
        await updateCompany.mutateAsync(data);
        toast.success(t('toast.updated'));
      } else {
        await createCompany.mutateAsync(data);
        toast.success(t('toast.created'));
      }
      onClose();
    } catch (_err) {
      toast.error(company ? t('toast.update_error') : t('toast.create_error'));
    }
  }

  const isPending = createCompany.isPending || updateCompany.isPending;

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={company ? t('form.edit_title') : t('form.create_title')}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 'var(--z-modal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
    >
      <div
        style={{
          background: 'var(--color-bg)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: 560,
          maxHeight: '90dvh',
          overflow: 'auto',
          boxShadow: 'var(--shadow-xl)',
          animation: 'page-enter 200ms var(--ease-out)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-5) var(--space-6)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
            {company ? t('form.edit_title') : t('form.create_title')}
          </h2>
          <button
            onClick={onClose}
            aria-label={t('actions.close', { ns: 'common' })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

            {/* Name */}
            <Field label={t('form.name_label')} error={errors.name} required>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('form.name_placeholder')}
                autoFocus
                style={inputStyle(!!errors.name)}
              />
            </Field>

            {/* Type */}
            <div>
              <label style={labelStyle}>{t('form.type_label')}</label>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
                {COMPANY_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleType(type)}
                    style={{
                      padding: 'var(--space-1) var(--space-3)',
                      border: form.type.includes(type) ? '1.5px solid var(--color-brand)' : '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--text-sm)',
                      color: form.type.includes(type) ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                      background: form.type.includes(type) ? 'var(--color-brand-pale)' : 'var(--color-bg)',
                      cursor: 'pointer',
                      fontWeight: form.type.includes(type) ? 'var(--font-medium)' : 'var(--font-regular)',
                    }}
                  >
                    {t(`types.${type}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Website + Phone row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Field label={t('form.website_label')} error={errors.website}>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder={t('form.website_placeholder')}
                  style={inputStyle(!!errors.website)}
                />
              </Field>
              <Field label={t('form.phone_label')}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  style={inputStyle(false)}
                />
              </Field>
            </div>

            {/* Email */}
            <Field label={t('form.email_label')}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={inputStyle(false)}
              />
            </Field>

            {/* Address */}
            <Field label={t('form.address_label')}>
              <input
                type="text"
                value={form.addressLine1}
                onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
                style={inputStyle(false)}
              />
            </Field>

            {/* City + State + ZIP */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--space-3)' }}>
              <Field label={t('form.city_label')}>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  style={inputStyle(false)}
                />
              </Field>
              <Field label={t('form.state_label')}>
                <input
                  type="text"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  style={inputStyle(false)}
                />
              </Field>
              <Field label={t('form.zip_label')}>
                <input
                  type="text"
                  value={form.zip}
                  onChange={(e) => setForm({ ...form, zip: e.target.value })}
                  style={inputStyle(false)}
                />
              </Field>
            </div>

            {/* Notes */}
            <Field label={t('form.notes_label')}>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                style={{ ...inputStyle(false), resize: 'vertical', fontFamily: 'var(--font-sans)' }}
              />
            </Field>
          </div>

          {/* Custom Fields */}
          {activeCustomFields.length > 0 && (
            <div style={{ padding: '0 var(--space-6) var(--space-4)' }}>
              <p className="label" style={{ marginBottom: 'var(--space-3)' }}>
                {t('section_custom_fields', { ns: 'customFields' })}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {activeCustomFields.map((field) => (
                  <Field key={field.id} label={field.label}>
                    <CustomFieldInput
                      field={field}
                      value={customFieldValues[field.key]}
                      onChange={(val) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: val }))}
                    />
                  </Field>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end',
              padding: 'var(--space-4) var(--space-6)',
              borderTop: '1px solid var(--color-border)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              style={{
                padding: 'var(--space-2) var(--space-5)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)',
                background: 'var(--color-bg)',
                cursor: 'pointer',
              }}
            >
              {t('actions.cancel', { ns: 'common' })}
            </button>
            <button
              type="submit"
              disabled={isPending}
              style={{
                padding: 'var(--space-2) var(--space-5)',
                background: 'var(--color-brand)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                opacity: isPending ? 0.7 : 1,
              }}
            >
              {isPending ? t('actions.loading', { ns: 'common' }) : t('actions.save', { ns: 'common' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  error,
  required,
}: {
  label: string;
  children: React.ReactNode;
  error?: string | undefined;
  required?: boolean | undefined;
}) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: 'var(--color-danger)', marginLeft: 2 }}>*</span>}
      </label>
      <div style={{ marginTop: 'var(--space-1)' }}>{children}</div>
      {error && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)', marginTop: 4 }}>{error}</p>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)',
  color: 'var(--color-text)',
  display: 'block',
};

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: 'var(--space-2) var(--space-3)',
    border: hasError ? '1px solid var(--color-danger)' : '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text)',
    background: 'var(--color-bg)',
    boxSizing: 'border-box',
  };
}
